import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import ProOnboardingChecklist from "@/components/ProOnboardingChecklist";
import {
  Bot, Sparkles, Loader2, Send, Map, Trophy, BookOpen, ShoppingBag, MapPinned,
  Building2, Megaphone, ChevronRight, Crown, Wand2, CheckCircle2, Coins,
  RefreshCw, Eye, EyeOff, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  ready:   { fr: "Prêt",    en: "Ready" },
  partial: { fr: "Partiel", en: "Partial" },
  soon:    { fr: "Bientôt", en: "Soon" },
};

const BUILDERS = [
  { key: "onboarding", icon: Bot, title_fr: "AI Onboarding", title_en: "AI Onboarding",
    desc_fr: "10 minutes de conversation avec geoVINUM AI, et votre fiche domaine est créée.", desc_en: "10 min conversation, full domain profile generated.",
    cta_fr: "Démarrer", cta_en: "Start", status: "ready", action: "onboarding" },
  { key: "tourism", icon: MapPinned, title_fr: "Wine Tourism Builder", title_en: "Wine Tourism Builder",
    desc_fr: "Parcours visiteurs : Découverte, Famille, Expert, Dégustation, Randonnée.", desc_en: "Visitor journeys: discovery, family, expert, tasting, hiking.",
    cta_fr: "Bientôt", cta_en: "Soon", status: "soon" },
  { key: "game", icon: Trophy, title_fr: "Game Builder", title_en: "Game Builder",
    desc_fr: "Quiz, chasse au trésor, memory, récompenses & avis Google.", desc_en: "Quizzes, treasure hunt, memory, rewards & Google reviews.",
    cta_fr: "Créer un jeu", cta_en: "Create a game", status: "ready", action: "sponsored" },
  { key: "story", icon: BookOpen, title_fr: "Story Builder", title_en: "Story Builder",
    desc_fr: "Histoire du domaine, parcelles, vins, anecdotes — racontées par geoVINUM AI.", desc_en: "Estate, plots, wines, anecdotes — narrated by geoVINUM AI.",
    cta_fr: "Générer", cta_en: "Generate", status: "ready", action: "story" },
  { key: "conversion", icon: Megaphone, title_fr: "Conversion Engine", title_en: "Conversion Engine",
    desc_fr: "Récompenses, bons d'achat, collecte email, avis Google.", desc_en: "Rewards, vouchers, email capture, Google reviews.",
    cta_fr: "Bientôt", cta_en: "Soon", status: "soon" },
  { key: "map", icon: Map, title_fr: "Map Builder & Designer", title_en: "Map Builder & Designer",
    desc_fr: "Parcours visite, carte parcellaire, affiches 360° print/web.", desc_en: "Tourism routes, plot map, 360° print/web posters.",
    cta_fr: "Concevoir", cta_en: "Design", status: "ready", action: "map_builder" },
];

export default function ProDashboard() {
  const { lang } = useT();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [obSession, setObSession] = useState(null);
  const [obMessages, setObMessages] = useState([]);
  const [obInput, setObInput] = useState("");
  const [obLoading, setObLoading] = useState(false);
  const [obReady, setObReady] = useState(false);
  const [obFinalized, setObFinalized] = useState(null);
  const [story, setStory] = useState(null);
  const [storyOpen, setStoryOpen] = useState(false);
  const [storyLoading, setStoryLoading] = useState(false);
  const [storyTarget, setStoryTarget] = useState(null);
  const scrollRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/pro/dashboard/overview");
      setOverview(data);
    } catch {
      toast.error(lang === "fr" ? "Connexion requise" : "Login required");
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [obMessages]);

  const startOnboarding = async () => {
    setObLoading(true);
    try {
      const { data } = await api.post("/pro/onboarding/start", { domain_hint: overview?.producer?.name || null });
      setObSession(data.session_id);
      setObMessages([{ role: "assistant", content: data.assistant }]);
    } catch (e) {
      toast.error(lang === "fr" ? "Démarrage impossible" : "Cannot start");
    } finally { setObLoading(false); }
  };

  const sendOb = async (e) => {
    e?.preventDefault();
    if (!obInput.trim() || obLoading) return;
    const userMsg = { role: "user", content: obInput.trim() };
    setObMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    const text = obInput.trim();
    setObInput("");
    setObLoading(true);
    try {
      const { data } = await api.post("/pro/onboarding/message", { session_id: obSession, message: text });
      setObMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: data.assistant };
        return copy;
      });
      setObReady(data.ready_to_finalize || false);
    } catch {
      setObMessages((m) => m.slice(0, -1));
      toast.error(lang === "fr" ? "Erreur geoVINUM AI" : "geoVINUM AI error");
    } finally { setObLoading(false); }
  };

  const finalizeOb = async () => {
    setObLoading(true);
    try {
      const { data } = await api.post("/pro/onboarding/finalize", { session_id: obSession, message: "" });
      setObFinalized(data.producer);
      toast.success(lang === "fr" ? "Fiche domaine créée 🎉" : "Domain profile created 🎉");
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "premium_only") {
        toast.error(lang === "fr" ? "Réservé aux abonnés Pro (49 €/mois)" : "Pro subscribers only (€49/mo)");
        navigate("/pricing?plan=pro");
      } else toast.error(lang === "fr" ? "Synthèse impossible" : "Synthesis failed");
    } finally { setObLoading(false); }
  };

  const handleBuilder = (b) => {
    if (b.action === "onboarding") {
      if (overview?.plan !== "pro") {
        toast.error(lang === "fr" ? "Réservé Pro" : "Pro only");
        navigate("/pricing?plan=pro");
        return;
      }
      startOnboarding();
    } else if (b.action === "sponsored") {
      navigate("/pro/sponsored");
    } else if (b.action === "map_builder") {
      if (overview?.plan !== "pro") {
        toast.error(lang === "fr" ? "Réservé Pro" : "Pro only");
        navigate("/pricing?plan=pro");
        return;
      }
      navigate("/pro/map-builder");
    } else if (b.action === "story") {
      openStoryBuilder();
    }
  };

  const openStoryBuilder = async () => {
    if (overview?.plan !== "pro") {
      toast.error(lang === "fr" ? "Réservé Pro" : "Pro only");
      navigate("/pricing?plan=pro");
      return;
    }
    if (!overview?.has_producer) {
      toast.error(lang === "fr" ? "Créez d'abord votre fiche domaine via AI Onboarding." : "Create your domain profile first via AI Onboarding.");
      return;
    }
    setStoryOpen(true);
    setStoryLoading(true);
    try {
      const { data } = await api.get("/pro/story/me");
      setStory(data?.story || { sections: {}, published: false });
    } catch {
      setStory({ sections: {}, published: false });
    } finally {
      setStoryLoading(false);
    }
  };

  const generateStory = async (section = "all") => {
    setStoryLoading(true);
    setStoryTarget(section);
    try {
      const { data } = await api.post("/pro/story/generate", { section });
      setStory(data.story);
      toast.success(lang === "fr" ? "Histoire générée ✨" : "Story generated ✨");
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "premium_only") {
        toast.error(lang === "fr" ? "Réservé aux abonnés Pro" : "Pro subscribers only");
      } else if (detail?.error === "insufficient_credits") {
        toast.error(lang === "fr" ? `Crédits insuffisants (${detail.cost} requis)` : `Not enough credits (${detail.cost} needed)`);
      } else if (detail?.error === "no_producer") {
        toast.error(lang === "fr" ? "Créez d'abord votre fiche domaine." : "Create domain profile first.");
      } else {
        toast.error(lang === "fr" ? "Échec de la génération" : "Generation failed");
      }
    } finally {
      setStoryLoading(false);
      setStoryTarget(null);
    }
  };

  const togglePublish = async () => {
    const next = !(story?.published);
    setStoryLoading(true);
    try {
      await api.post("/pro/story/publish", { published: next });
      setStory((s) => ({ ...s, published: next }));
      toast.success(next
        ? (lang === "fr" ? "Histoire publiée 🚀" : "Story published 🚀")
        : (lang === "fr" ? "Histoire dépubliée" : "Story unpublished"));
    } catch {
      toast.error(lang === "fr" ? "Action impossible" : "Action failed");
    } finally {
      setStoryLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16" data-testid="pro-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="max-w-2xl">
          <span className="chip-pink">{lang === "fr" ? "Espace Pro" : "Pro space"}</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">Dashboard</h1>
          <p className="text-[#4A5568] mt-3">
            {lang === "fr"
              ? "geoVINUM AI vous équipe : créez votre fiche domaine, vos parcours, vos jeux, votre carte. Tout en quelques clics."
              : "geoVINUM AI equips you: create your estate profile, journeys, games, map. All in a few clicks."}
          </p>
        </div>
        {overview && (
          <div className="grid grid-cols-3 gap-3 text-center" data-testid="pro-dashboard-stats">
            <Stat label={lang === "fr" ? "Crédits" : "Credits"} value={overview.credits} icon={Coins} />
            <Stat label={lang === "fr" ? "Quiz" : "Quizzes"} value={overview.sponsored_count} icon={Trophy} />
            <Stat label="Leads" value={overview.leads_count} icon={Megaphone} />
          </div>
        )}
      </div>

      {overview?.plan !== "pro" && (
        <div className="mt-8 rounded-2xl border border-[#1F3A5F]/15 bg-gradient-to-br from-[#FCE4ED] via-white to-[#FBF7F3] p-5 flex items-center gap-4 flex-wrap" data-testid="pro-dashboard-upsell">
          <Crown className="w-6 h-6 text-[#1F3A5F]" />
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-display text-xl text-[#1F3A5F]">{lang === "fr" ? "Débloquez tous les Builders" : "Unlock all Builders"}</h3>
            <p className="text-sm text-[#4A5568]">{lang === "fr" ? "Plan Pro 49 €/mois + 300 crédits inclus." : "Pro plan €49/mo + 300 credits included."}</p>
          </div>
          <Link to="/pricing?plan=pro" className="px-4 py-2 rounded-xl bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]">
            {lang === "fr" ? "Passer Pro" : "Upgrade to Pro"}
          </Link>
        </div>
      )}

      <div className="mt-8">
        <ProOnboardingChecklist />
      </div>

      {/* Builders grid */}
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="pro-builders">
        {BUILDERS.map((b) => {
          const Icon = b.icon;
          const enabled = b.status !== "soon";
          return (
            <button
              key={b.key}
              data-testid={`pro-builder-${b.key}`}
              onClick={() => enabled && handleBuilder(b)}
              disabled={!enabled}
              className={`text-left rounded-2xl border p-5 transition-all ${
                enabled
                  ? "border-[#1F3A5F]/12 bg-white hover:border-[#E5276A] hover:shadow-md cursor-pointer"
                  : "border-[#1F3A5F]/8 bg-[#FBF7F3] cursor-not-allowed opacity-75"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${enabled ? "bg-[#FCE4ED] text-[#E5276A]" : "bg-white text-[#8B95A1]"}`}>
                  <Icon className="w-5 h-5" />
                </div>
                {b.status === "partial" && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{STATUS_LABEL.partial[lang] || STATUS_LABEL.partial.en}</span>}
                {b.status === "soon" && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-[#1F3A5F]/10 text-[#1F3A5F]">{STATUS_LABEL.soon[lang] || STATUS_LABEL.soon.en}</span>}
                {b.status === "ready" && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">{STATUS_LABEL.ready[lang] || STATUS_LABEL.ready.en}</span>}
              </div>
              <h3 className="font-display text-lg text-[#1F3A5F] mt-3">{lang === "fr" ? b.title_fr : b.title_en}</h3>
              <p className="text-xs text-[#4A5568] mt-1 leading-relaxed">{lang === "fr" ? b.desc_fr : b.desc_en}</p>
              {enabled && (
                <div data-testid={`pro-builder-cta-${b.key}`} className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-[#E5276A]">
                  {lang === "fr" ? b.cta_fr : b.cta_en} <ChevronRight className="w-3 h-3" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* AI Onboarding chat */}
      {obSession && (
        <div className="mt-10 rounded-2xl border-2 border-[#E5276A]/30 bg-white p-5" data-testid="pro-onboarding-chat">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#E5276A]" />
            <h2 className="font-display text-xl text-[#1F3A5F]">{lang === "fr" ? "AI Onboarding · geoVINUM AI vous interview" : "AI Onboarding · geoVINUM AI is interviewing you"}</h2>
          </div>

          {!obFinalized ? (
            <>
              <div ref={scrollRef} className="mt-4 h-80 overflow-y-auto bg-[#FBF7F3] rounded-xl p-3 space-y-2">
                {obMessages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-[#E5276A] text-white" : "bg-white text-[#1F3A5F] border border-[#1F3A5F]/10"}`}>
                      {m.role === "assistant" && !m.content ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>}
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={sendOb} className="mt-3 flex gap-2">
                <input
                  data-testid="pro-onboarding-input"
                  value={obInput}
                  onChange={(e) => setObInput(e.target.value)}
                  placeholder={lang === "fr" ? "Votre réponse…" : "Your answer…"}
                  className="flex-1 px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]"
                  disabled={obLoading}
                />
                <button type="submit" data-testid="pro-onboarding-send" disabled={obLoading || !obInput.trim()} className="px-4 py-2 rounded-lg bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50">
                  {obLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
              {obReady && (
                <button data-testid="pro-onboarding-finalize" onClick={finalizeOb} disabled={obLoading} className="mt-3 w-full px-4 py-3 rounded-xl bg-[#1F3A5F] text-white font-bold hover:bg-[#2A4870] inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {obLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {lang === "fr" ? "Synthétiser et créer ma fiche domaine" : "Synthesise & create my profile"}
                </button>
              )}
            </>
          ) : (
            <div className="mt-4" data-testid="pro-onboarding-success">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="w-5 h-5" />
                <h3 className="font-display text-lg">{lang === "fr" ? "Fiche créée !" : "Profile created!"}</h3>
              </div>
              <p className="text-sm text-[#4A5568] mt-2">
                <b className="text-[#1F3A5F]">{obFinalized.name}</b>{obFinalized.region ? ` · ${obFinalized.region}` : ""}
              </p>
              {obFinalized.description && <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">{obFinalized.description.slice(0, 300)}…</p>}
              <Link to={`/producers/${obFinalized.slug}`} className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#E5276A]">
                {lang === "fr" ? "Voir ma fiche publique" : "View my public profile"} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Story Builder panel */}
      {storyOpen && (
        <div className="mt-10 rounded-2xl border-2 border-[#1F3A5F]/20 bg-white p-5 md:p-7" data-testid="pro-story-builder">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#E5276A]" />
              <h2 className="font-display text-xl text-[#1F3A5F]">{lang === "fr" ? "Story Builder · L'histoire racontée par geoVINUM AI" : "Story Builder · Your domain, narrated by geoVINUM AI"}</h2>
            </div>
            <button onClick={() => setStoryOpen(false)} className="text-xs text-[#8B95A1] hover:text-[#1F3A5F] font-bold" data-testid="story-close">{lang === "fr" ? "Fermer" : "Close"}</button>
          </div>

          {!story || Object.keys(story.sections || {}).length === 0 ? (
            <div className="mt-5 p-4 rounded-xl bg-[#FBF7F3] border border-[#1F3A5F]/10">
              <div className="flex items-center gap-2 text-[#1F3A5F]">
                <AlertCircle className="w-4 h-4 text-[#E5276A]" />
                <p className="text-sm font-medium">
                  {lang === "fr"
                    ? "Aucune histoire générée pour l'instant. Lance la génération complète (6 sections · ~6 crédits)."
                    : "No story yet. Generate the full pack (6 sections · ~6 credits)."}
                </p>
              </div>
              <button
                data-testid="story-generate-all"
                onClick={() => generateStory("all")}
                disabled={storyLoading}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-50"
              >
                {storyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {lang === "fr" ? "Générer toute l'histoire" : "Generate full story"}
              </button>
            </div>
          ) : (
            <>
              <div className="mt-4 flex items-center flex-wrap gap-3">
                <button
                  data-testid="story-regenerate-all"
                  onClick={() => generateStory("all")}
                  disabled={storyLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1F3A5F] text-white text-xs font-bold hover:bg-[#2A4870] disabled:opacity-60"
                >
                  {storyLoading && storyTarget === "all" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  {lang === "fr" ? "Tout régénérer" : "Regenerate all"}
                </button>
                <button
                  data-testid="story-toggle-publish"
                  onClick={togglePublish}
                  disabled={storyLoading}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-60 ${
                    story.published ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-[#FCE4ED] text-[#E5276A] hover:bg-[#FBD0DC]"
                  }`}
                >
                  {story.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {story.published
                    ? (lang === "fr" ? "Publiée · cliquer pour cacher" : "Published · click to hide")
                    : (lang === "fr" ? "Publier sur ma fiche" : "Publish on profile")}
                </button>
                {overview?.producer?.slug && (
                  <Link to={`/producers/${overview.producer.slug}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#1F3A5F] underline">
                    {lang === "fr" ? "Voir ma fiche publique" : "View public profile"} <ChevronRight className="w-3 h-3" />
                  </Link>
                )}
              </div>

              <div className="mt-6 space-y-4">
                {["hero", "history", "terroir", "wines", "philosophy", "visit", "anecdote"].map((key) => {
                  const sec = story.sections?.[key];
                  if (!sec || !sec.body) return null;
                  return (
                    <div key={key} className="rounded-xl border border-[#1F3A5F]/10 bg-[#FBF7F3] p-4" data-testid={`story-section-${key}`}>
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-display text-lg text-[#1F3A5F]">{sec.title}</h3>
                        <button
                          data-testid={`story-regen-${key}`}
                          onClick={() => generateStory(key)}
                          disabled={storyLoading}
                          title={lang === "fr" ? "Régénérer cette section" : "Regenerate this section"}
                          className="p-1.5 rounded-md text-[#8B95A1] hover:text-[#E5276A] hover:bg-white disabled:opacity-40"
                        >
                          {storyLoading && storyTarget === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      <p className="mt-2 text-sm text-[#4A5568] leading-relaxed whitespace-pre-wrap">{sec.body}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="rounded-xl bg-white border border-[#1F3A5F]/12 px-4 py-2">
    <Icon className="w-4 h-4 text-[#E5276A] mx-auto" />
    <div className="text-xl font-display text-[#1F3A5F]">{value ?? "—"}</div>
    <div className="text-[10px] uppercase tracking-wider font-bold text-[#8B95A1]">{label}</div>
  </div>
);
